import { z } from "zod";
import { healthBehaviourSchema } from "./health-behaviours.js";
import { snapshotDrillIdSchema } from "./snapshot-drills.js";
// One goal at a time, chosen from the journey catalog, measured against the
// member's own series. It orders what Heartbeat already earned; it never fires
// a moment and never changes a rung.
/** Six weeks, then it lapses unless the member renews it. */
export const SNAPSHOT_GOAL_WEEKS = 6;
/** The share of a window a goal asks for before it counts as held. */
const GOAL_NEED_SHARE = 0.7;
export function snapshotGoalNeed(windowDays) {
    return Math.max(1, Math.ceil(windowDays * GOAL_NEED_SHARE));
}
export function snapshotGoalUntil(startedAt) {
    return new Date(startedAt.getTime() + SNAPSHOT_GOAL_WEEKS * 7 * 86_400_000);
}
export const snapshotGoalSchema = z.object({
    outcomeId: z.string(),
    behaviour: healthBehaviourSchema,
    /** The drill that grades this behaviour, when one does. */
    drill: snapshotDrillIdSchema.nullable(),
    label: z.string(),
    supporting: z.string(),
    target: z.number(),
    direction: z.enum(["gte", "lte", "eq"]),
    /** The target's own unit, so a gap reads "8 min", not "8". */
    unit: z.string(),
    noun: z.string(),
    windowDays: z.number().int(),
    /** How many qualifying days the goal asks for inside its window. */
    need: z.number().int(),
    startedAt: z.string(),
    until: z.string(),
    /** One value per day of the window, oldest first; null where nothing reported. */
    series: z.array(z.number().nullable()).nullable(),
    /** The window's own statistic, the one the target is read against. */
    stat: z.number().nullable(),
    /** That statistic as a sentence the member can check. */
    read: z.string().nullable(),
    /** Days inside the window that met the target. */
    met: z.number().int(),
    /** Whether the window as a whole is on target. */
    onTarget: z.boolean(),
    /** The gap to the target as the member reads it, when there is one. */
    gap: z.string().nullable(),
    /** Said out loud when the member's own data moved a different behaviour most. */
    conflict: z.string().nullable(),
});
/** What a goal looks like before any reading has landed against it. */
export function snapshotGoalIsForming(goal) {
    return goal.series === null || goal.stat === null;
}
export function snapshotGoalDayMet(value, target, direction) {
    if (direction === "lte")
        return value <= target;
    if (direction === "eq")
        return value === target;
    return value >= target;
}
/** The one memory key a goal lives under. One active fact, so one goal. */
export const SNAPSHOT_INTENT_KEY = "intent.current";
/** The active goal, or null when there is none or its window has closed. */
export function readSnapshotIntent(payload, now) {
    if (!payload || typeof payload !== "object")
        return null;
    const p = payload;
    if (p.kind !== "intent")
        return null;
    const { outcomeId, behaviour, label, until } = p;
    if (typeof outcomeId !== "string" || typeof label !== "string" || typeof until !== "string") {
        return null;
    }
    if (typeof behaviour !== "string")
        return null;
    // A lapse is silent: the goal simply stops being current, and nothing nags.
    if (Date.parse(until) <= now.getTime())
        return null;
    return { outcomeId, behaviour: behaviour, label, until };
}
/**
 * Keys the system addresses by name rather than by reading.
 *
 * Consolidation merges near-duplicate facts by superseding the losers and
 * pointing them at one survivor, which is right for extracted impressions and
 * wrong for these: the survivor carries a different key, so every reader that
 * asks for this one by name gets nothing. A member's goal disappeared that way.
 * A reserved key is never a merge candidate and never a loser.
 */
export const RESERVED_FACT_KEYS = new Set([SNAPSHOT_INTENT_KEY]);
//# sourceMappingURL=snapshot-goal.js.map